
































































import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace } from "vuex-class";
import FscPageHeader from "@/components/FscPageHeader.vue";
import CrudService from "@/services/CrudService";
import { AxiosResponse } from "axios";
import { formatStudentName } from "@/utils/NameUtil";
import { IStudentEducation } from "@/interfaces/IStudentEducation";
import { IPraxisOverview, IPraxisRoute } from "@/interfaces/Student/IKiPilotPraxis";
import type { IMapFocusRequest, LatLngTuple } from "@/components/Map/BaseLeafletMap.vue";
import KiPilotPraxisMap from "@/views/Student/KiPilotPraxis/KiPilotPraxisMap.vue";
import KiPilotPraxisRouteList from "@/views/Student/KiPilotPraxis/KiPilotPraxisRouteList.vue";
import KiPilotPraxisTasks from "@/views/Student/KiPilotPraxis/KiPilotPraxisTasks.vue";
import KiPilotPraxisRouteDetails from "@/views/Student/KiPilotPraxis/KiPilotPraxisRouteDetails.vue";
import KiPilotPraxisEducationStatus from "@/views/Student/KiPilotPraxis/KiPilotPraxisEducationStatus.vue";

const crudService = new CrudService();
const StudentModule = namespace("student");

const EMPTY_OVERVIEW: IPraxisOverview = {
  summary: { totalDistanceInMeters: 0, totalTimeInSeconds: 0 },
  routes: [],
  taskGroups: [],
};

@Component({
  components: {
    FscPageHeader,
    KiPilotPraxisMap,
    KiPilotPraxisRouteList,
    KiPilotPraxisTasks,
    KiPilotPraxisRouteDetails,
    KiPilotPraxisEducationStatus,
  },
})
export default class KiPilotPraxis extends Vue {
  public name = "KiPilotPraxis";

  @StudentModule.Action("findOne")
  public findStudentById: any;

  @StudentModule.Getter("getDataItem")
  public student: any;

  public studentEducations: Array<IStudentEducation> = [];
  public activeEducationIndex = 0;
  public overview: IPraxisOverview = EMPTY_OVERVIEW;
  public selectedRouteId: number | null = null;
  public highlightedTaskId: number | null = null;
  public highlightedMarkId: number | null = null;
  public focusRequest: IMapFocusRequest | null = null;
  public loading = false;
  private focusNonce = 0;

  public async mounted(): Promise<void> {
    await this.findStudentById({ resource: "students", id: this.studentId });
    await this.findStudentEducations();
    await this.loadOverview();
  }

  protected get studentId(): number {
    const { id } = this.$route.params;
    return Number(id);
  }

  protected get studentFullName(): string {
    return this.student ? formatStudentName(this.student.firstName, this.student.lastName) : "";
  }

  protected get activeStudentEducationId(): number | null {
    return this.studentEducations[this.activeEducationIndex]?.id ?? null;
  }

  public get selectedRoute(): IPraxisRoute | null {
    if (!this.selectedRouteId) return null;
    return this.overview.routes.find((route) => route.routeId === this.selectedRouteId) || null;
  }

  protected async findStudentEducations(): Promise<void> {
    const response: AxiosResponse = await crudService.findAll({
      resource: `student-educations/student/${this.studentId}`,
      descriptionField: "",
    });
    this.studentEducations = response.data;
  }

  @Watch("activeEducationIndex")
  public onEducationChanged(): void {
    this.loadOverview();
  }

  public onSelectTask(taskId: number, markId?: number): void {
    this.highlightedTaskId = taskId;
    this.highlightedMarkId = markId ?? null;
  }

  public onFocusPoints(points: Array<LatLngTuple>): void {
    this.focusNonce += 1;
    this.focusRequest = { points, nonce: this.focusNonce };
  }

  // Clear stale highlights whenever the route scope they were shown against changes.
  @Watch("selectedRouteId")
  public onSelectedRouteIdChanged(): void {
    this.highlightedTaskId = null;
    this.highlightedMarkId = null;
  }

  // Strictly loaded once per selected education; route selection afterwards is frontend-only (no further requests).
  protected async loadOverview(): Promise<void> {
    if (!this.activeStudentEducationId) {
      this.overview = EMPTY_OVERVIEW;
      return;
    }

    this.loading = true;
    this.selectedRouteId = null;
    try {
      const response: AxiosResponse = await crudService.findAll({
        resource: `driving-lesson-routes/student-education/${this.activeStudentEducationId}/praxis-overview`,
        descriptionField: "",
      });
      this.overview = response.data;
    } finally {
      this.loading = false;
    }
  }
}
